import * as React from "react";
import { createRoot } from "react-dom/client";
// Fix: Use named import for Index to resolve the "no default export" error from Index.tsx
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./config/queryClient";
import { PostHogProvider } from "posthog-js/react";

import "./i18n";
import { initLanguageSync } from "./services/languageSyncService";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { SmallScreenOverlay } from "./components/SmallScreenOverlay";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = import.meta.env.VITE_ENVIRONMENT;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.browserProfilingIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Attach sentry-trace and baggage headers to outgoing fetch/XHR requests
    // targeting our Cloud Functions, so backend spans join the same trace.
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/europe-west3-imagestudio-a7b2c\.cloudfunctions\.net/,
      /^https:\/\/europe-west3-imagestudio-prod\.cloudfunctions\.net/,
      /^https:\/\/.*\.run\.app/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // Set profilesSampleRate to 1.0 to enable Sentry browser profiling
    profilesSampleRate: 1.0,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
  });
}

const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  ui_host: "https://eu.posthog.com",
  defaults: "2025-11-30",
} as const;

// Keep the User row's language column in sync for automatic emails
// (SIS-483). Must run once at startup, before any auth state resolves.
initLanguageSync();

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = createRoot(rootElement);

root.render(
  <React.StrictMode>
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={posthogOptions}
    >
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          {/* Global blocking overlay for viewports too small for the
              desktop-first UI. Mounted here (outside the router) so it
              covers every route and auth state. */}
          <SmallScreenOverlay />
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>
);
